// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { Shield } from 'lucide-react';

import { cn } from '@/lib/utils';

import { AppIconTile } from '@/components/greenlight/AppIconTile';
import { IntegrationTile } from '@/components/greenlight/IntegrationTile';

export type KnowledgeTileScope = 'org' | 'integration' | 'app';

export interface ScopeTileProps {
  scope: KnowledgeTileScope;
  integrationSlug?: string;
  integrationName?: string;
  appName?: string;
  appSlug?: string;
  /** Tile side in px. Default 40. */
  size?: number;
  className?: string;
}

/**
 * Scope-aware icon tile for Knowledge entry rows, proposal rows, and sheet headers.
 * Delegates to IntegrationTile, AppIconTile, or a Shield glyph tile depending on scope.
 * Purely decorative — the adjacent label carries the accessible meaning.
 */
export function ScopeTile({
  scope,
  integrationSlug,
  integrationName,
  appName,
  appSlug,
  size = 40,
  className,
}: ScopeTileProps): React.JSX.Element {
  if (scope === 'integration') {
    return (
      <IntegrationTile
        slug={integrationSlug ?? ''}
        displayName={integrationName ?? integrationSlug ?? 'Integration'}
        size={size}
        className={className}
      />
    );
  }

  if (scope === 'app') {
    return (
      <AppIconTile
        name={appName ?? appSlug ?? 'App'}
        slug={appSlug}
        size={size}
        className={className}
      />
    );
  }

  // scope === 'org'
  return (
    <div
      aria-hidden="true"
      className={cn(
        'inline-flex items-center justify-center rounded-gl-icon border border-border bg-muted/60 shrink-0',
        className,
      )}
      style={{ width: size, height: size }}
    >
      <Shield size={Math.round(size * 0.45)} className="text-muted-foreground" aria-hidden />
    </div>
  );
}
